import React from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { List } from "@refinedev/mui";
import { axiosInstance } from "@/providers/data";

const RESOURCE = "admin/compliance";

// GET /api/v2/admin/compliance/disclosure/gaps — a session the server saw
// start with no first-message disclosure on record. Empty is the goal. `flow`
// says whose play it was: core's (v1) or a Story Studio play-through (v2); a
// server from before 2026-09-30 sends no flow, and every row is then v1.
type UndisclosedSession = {
  sessionId: string;
  userId: number | null;
  storyId: number | null;
  startedAt: string;
  flow?: "v1" | "v2";
};

type DisclosureTrigger = "A" | "B" | "C" | "R";

// GET /api/v2/admin/compliance/disclosure/session/:sessionId — one fire.
// `knownMinor` is the server's own stamp on arrival; null on rows from before
// it existed (migration 0085), absent from a server that predates it.
type DisclosureRepeat = {
  trigger: DisclosureTrigger;
  activeSeconds: number | null;
  knownMinor?: boolean | null;
  firedAt: string;
  receivedAt: string;
};

// GET /api/v2/admin/compliance/disclosure/break-gaps — a known minor's 3-hour
// repeat with no break reminder at the same active time. Empty is the goal.
type BreakReminderGap = {
  sessionId: string;
  userId: number | null;
  storyId: number | null;
  activeSeconds: number;
  firedAt: string;
  receivedAt: string;
};

const TRIGGER_LABEL: Record<DisclosureTrigger, string> = {
  A: "A · first message",
  B: "B · 3-hour repeat",
  C: "C · chip tap",
  R: "R · break reminder",
};

// GET /api/v2/admin/compliance/crisis/summary — one (day, story) cell.
type CrisisSummaryRow = {
  day: string;
  storyId: number | null;
  fires: number;
  classified: number;
};

// GET /api/v2/admin/compliance/consent/:userId — one ledger row. `userId` is
// null once the account is deleted: the row outlives it as an anonymised fact.
type ConsentEvent = {
  id: number;
  userId: number | null;
  kind: "mature_enable" | "mature_disable" | "art9_explicit";
  sheetVersion: string | null;
  clientBuild: string | null;
  clientPlatform: string | null;
  sourceIp: string | null;
  at: string;
};

type Envelope<T> = { data: T[]; meta: { total: number; fires?: number } };

const isoDate = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const formatWhen = (value: string) => new Date(value).toLocaleString();

// The shared axios instance rejects with Refine's plain `HttpError` object, not
// an `Error` instance, so `instanceof Error` would hide the server's message.
const errorMessage = (err: unknown): string | undefined => {
  if (err instanceof Error) {
    return err.message;
  }
  if (err && typeof err === "object" && "message" in err && typeof err.message === "string") {
    return err.message;
  }
  return undefined;
};

/**
 * One fetch, one table. Every artefact here is a custom read outside Refine's
 * paginated `getList` shape, so each panel owns its request through the shared
 * axios instance (token transfer is header-based; a plain fetch would arrive
 * unauthenticated). Read-only by construction: nothing on this page writes.
 */
function useArtefact<T>(path: string | null, params?: Record<string, string>) {
  const [rows, setRows] = React.useState<T[] | null>(null);
  const [meta, setMeta] = React.useState<Envelope<T>["meta"] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const paramsKey = JSON.stringify(params ?? {});

  const load = React.useCallback(async () => {
    if (!path) {
      return;
    }
    setBusy(true);
    setError(null);
    // Drop the previous result first: a lookup for a new session or user must
    // never show the old one's evidence under the new identifier while the
    // request is in flight.
    setRows(null);
    setMeta(null);
    try {
      const response = await axiosInstance.get<Envelope<T>>(`/api/v2/${RESOURCE}/${path}`, {
        params: JSON.parse(paramsKey) as Record<string, string>,
      });
      setRows(response.data.data);
      setMeta(response.data.meta);
    } catch (err) {
      setError(errorMessage(err) ?? "Could not load the artefact.");
      setRows(null);
      setMeta(null);
    } finally {
      setBusy(false);
    }
  }, [path, paramsKey]);

  return { rows, meta, error, busy, load };
}

/** From/to pickers shared by the two windowed artefacts. A date-only `to` means the whole of that day server-side. */
const WindowPicker = ({
  from,
  to,
  onFrom,
  onTo,
  onRun,
  busy,
}: {
  from: string;
  to: string;
  onFrom: (value: string) => void;
  onTo: (value: string) => void;
  onRun: () => void;
  busy: boolean;
}) => (
  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
    <TextField
      type="date"
      size="small"
      label="From"
      value={from}
      onChange={(event) => onFrom(event.target.value)}
      InputLabelProps={{ shrink: true }}
    />
    <TextField
      type="date"
      size="small"
      label="To"
      value={to}
      onChange={(event) => onTo(event.target.value)}
      InputLabelProps={{ shrink: true }}
    />
    <Button variant="outlined" size="small" onClick={onRun} disabled={busy}>
      {busy ? "Loading…" : "Run"}
    </Button>
  </Stack>
);

const defaultWindow = () => {
  const today = new Date();
  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  return { from: isoDate(weekAgo), to: isoDate(today) };
};

/**
 * A week whose end closed at least a full day ago, for a report whose rows can still be on their way from a device.
 * The server reads a date-only `to` as the end of that UTC day, so the latest such `to` is the UTC date of 48 hours
 * ago.
 */
const settledWindow = () => {
  const day = 24 * 60 * 60 * 1000;
  const end = new Date(Date.now() - 2 * day);
  const start = new Date(end.getTime() - 7 * day);
  return { from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) };
};

/**
 * Sessions in the window with no first-message disclosure. This is the
 * reconciliation the disclosure log exists for: the server's own session-start
 * marker joined to the client's trigger-A report, listing the sessions with
 * none. Each row is either a client bug or a session that never reached its
 * first message — review with the app team, never dismiss in bulk.
 */
const DisclosureGaps = () => {
  const initial = React.useMemo(defaultWindow, []);
  const [from, setFrom] = React.useState(initial.from);
  const [to, setTo] = React.useState(initial.to);
  const { rows, meta, error, busy, load } = useArtefact<UndisclosedSession>("disclosure/gaps", { from, to });

  const columns = React.useMemo<GridColDef<UndisclosedSession>[]>(
    () => [
      { field: "sessionId", headerName: "Session", flex: 1, minWidth: 220, sortable: false },
      {
        field: "userId",
        headerName: "User",
        minWidth: 90,
        sortable: false,
        renderCell: ({ row }) => (row.userId == null ? <span style={{ opacity: 0.6 }}>-</span> : <code>{row.userId}</code>),
      },
      {
        field: "storyId",
        headerName: "Story",
        minWidth: 90,
        sortable: false,
        renderCell: ({ row }) => (row.storyId == null ? <span style={{ opacity: 0.6 }}>-</span> : <code>{row.storyId}</code>),
      },
      {
        field: "startedAt",
        headerName: "Started",
        minWidth: 190,
        sortable: false,
        renderCell: ({ row }) => <span>{formatWhen(row.startedAt)}</span>,
      },
      {
        field: "flow",
        headerName: "Play",
        minWidth: 110,
        sortable: false,
        renderCell: ({ row }) => (
          <Chip size="small" variant="outlined" label={row.flow === "v2" ? "Studio (v2)" : "Core (v1)"} />
        ),
      },
    ],
    []
  );

  return (
    <Stack spacing={1}>
      <Typography variant="h6">Disclosure gaps</Typography>
      <Typography variant="caption" color="text.secondary">
        Sessions started in the window with no first-message AI disclosure on record (EU AI Act Art 50): core
        sessions (v1) and Story Studio play-throughs and character chats (v2). Empty is the goal.
      </Typography>
      <WindowPicker from={from} to={to} onFrom={setFrom} onTo={setTo} onRun={load} busy={busy} />
      {error && <Alert severity="error">{error}</Alert>}
      {rows && (
        <>
          <Chip
            size="small"
            variant="outlined"
            color={rows.length === 0 ? "success" : "warning"}
            label={rows.length === 0 ? "No gaps in this window" : `${meta?.total ?? rows.length} undisclosed session(s)`}
            sx={{ alignSelf: "flex-start" }}
          />
          <DataGrid
            rows={rows}
            getRowId={(row) => `${row.flow ?? "v1"}:${row.sessionId}`}
            columns={columns}
            autoHeight
            disableColumnFilter
            disableRowSelectionOnClick
            pageSizeOptions={[25, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          />
        </>
      )}
    </Stack>
  );
};

/**
 * A known minor's 3-hour repeats with no break reminder beside them (SB 243): every B the server received in the
 * window for an account it knew as a minor, with no R in the same session at the same active time. The client sends
 * the two together, so each row is a reminder that was not shown or not logged. An R still waiting in a client's
 * outbox shows here until it lands, so read a window that closed at least a day ago.
 */
const BreakReminderGaps = () => {
  const initial = React.useMemo(settledWindow, []);
  const [from, setFrom] = React.useState(initial.from);
  const [to, setTo] = React.useState(initial.to);
  const { rows, meta, error, busy, load } = useArtefact<BreakReminderGap>("disclosure/break-gaps", { from, to });

  const columns = React.useMemo<GridColDef<BreakReminderGap & { id: string }>[]>(
    () => [
      { field: "sessionId", headerName: "Session", flex: 1, minWidth: 220, sortable: false },
      {
        field: "userId",
        headerName: "User",
        minWidth: 90,
        sortable: false,
        renderCell: ({ row }) => (row.userId == null ? <span style={{ opacity: 0.6 }}>-</span> : <code>{row.userId}</code>),
      },
      {
        field: "storyId",
        headerName: "Story",
        minWidth: 90,
        sortable: false,
        renderCell: ({ row }) => (row.storyId == null ? <span style={{ opacity: 0.6 }}>-</span> : <code>{row.storyId}</code>),
      },
      {
        field: "activeSeconds",
        headerName: "Active time",
        minWidth: 130,
        sortable: false,
        renderCell: ({ row }) => <span>{`${(row.activeSeconds / 3600).toFixed(2)} h`}</span>,
      },
      {
        field: "receivedAt",
        headerName: "B received (server clock)",
        minWidth: 200,
        sortable: false,
        renderCell: ({ row }) => <span>{formatWhen(row.receivedAt)}</span>,
      },
    ],
    []
  );

  return (
    <Stack spacing={1}>
      <Typography variant="h6">Break reminder gaps</Typography>
      <Typography variant="caption" color="text.secondary">
        3-hour repeats for accounts the server knew as minors with no break reminder at the same active time (SB
        243). Empty is the goal. Read a window that closed at least a day ago: a reminder can still be on its way from
        a device.
      </Typography>
      <WindowPicker from={from} to={to} onFrom={setFrom} onTo={setTo} onRun={load} busy={busy} />
      {error && <Alert severity="error">{error}</Alert>}
      {rows && (
        <>
          <Chip
            size="small"
            variant="outlined"
            color={rows.length === 0 ? "success" : "warning"}
            label={rows.length === 0 ? "No gaps in this window" : `${meta?.total ?? rows.length} missing reminder(s)`}
            sx={{ alignSelf: "flex-start" }}
          />
          <DataGrid
            rows={rows.map((row) => ({ ...row, id: `${row.sessionId}:${row.activeSeconds}:${row.receivedAt}` }))}
            columns={columns}
            autoHeight
            disableColumnFilter
            disableRowSelectionOnClick
            pageSizeOptions={[25, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          />
        </>
      )}
    </Stack>
  );
};

/** One session's disclosure timeline — the NY Art 47 three-hour check, read off the B rows' active-seconds. */
const SessionTimeline = () => {
  const [input, setInput] = React.useState("");
  const [sessionId, setSessionId] = React.useState<string | null>(null);
  const { rows, error, busy, load } = useArtefact<DisclosureRepeat>(
    sessionId ? `disclosure/session/${encodeURIComponent(sessionId)}` : null
  );

  React.useEffect(() => {
    if (sessionId) {
      void load();
    }
  }, [sessionId, load]);

  const columns = React.useMemo<GridColDef<DisclosureRepeat & { id: number }>[]>(
    () => [
      {
        field: "trigger",
        headerName: "Trigger",
        minWidth: 120,
        sortable: false,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            variant="outlined"
            color={row.trigger === "R" ? "info" : "default"}
            label={TRIGGER_LABEL[row.trigger] ?? row.trigger}
          />
        ),
      },
      {
        field: "activeSeconds",
        headerName: "Active time",
        minWidth: 130,
        sortable: false,
        renderCell: ({ row }) =>
          row.activeSeconds == null ? (
            <span style={{ opacity: 0.6 }}>-</span>
          ) : (
            <span>{`${(row.activeSeconds / 3600).toFixed(2)} h`}</span>
          ),
      },
      {
        field: "knownMinor",
        headerName: "Known minor",
        minWidth: 120,
        sortable: false,
        renderCell: ({ row }) =>
          row.knownMinor == null ? (
            <span style={{ opacity: 0.6 }}>-</span>
          ) : (
            <span>{row.knownMinor ? "yes" : "no"}</span>
          ),
      },
      {
        field: "firedAt",
        headerName: "Fired (client clock)",
        minWidth: 190,
        sortable: false,
        renderCell: ({ row }) => <span>{formatWhen(row.firedAt)}</span>,
      },
      {
        field: "receivedAt",
        headerName: "Received (server clock)",
        minWidth: 190,
        sortable: false,
        renderCell: ({ row }) => <span>{formatWhen(row.receivedAt)}</span>,
      },
    ],
    []
  );

  return (
    <Stack spacing={1}>
      <Typography variant="h6">Session disclosure timeline</Typography>
      <Typography variant="caption" color="text.secondary">
        Every disclosure fire for one session (a core session id, or a Studio play-through's story id), in
        client-clock order. Trigger B rows should sit at ~3h multiples of active time (NY GBL Art 47); for a known
        minor each B has an R beside it at the same active time (SB 243). "Known minor" is what the server knew
        when the fire arrived; "-" is a row from before it recorded that.
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          size="small"
          label="Session id"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              setSessionId(input.trim() || null);
            }
          }}
          sx={{ minWidth: 320 }}
        />
        <Button variant="outlined" size="small" onClick={() => setSessionId(input.trim() || null)} disabled={busy}>
          Look up
        </Button>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {rows && sessionId && (
        <DataGrid
          rows={rows.map((row, id) => ({ ...row, id }))}
          columns={columns}
          autoHeight
          disableColumnFilter
          disableRowSelectionOnClick
          hideFooter
          localeText={{ noRowsLabel: "No disclosure on record for this session" }}
        />
      )}
    </Stack>
  );
};

/** The SB 243 aggregate — fires per day per story. Reads no user column, so it survives account deletion unchanged. */
const CrisisSummary = () => {
  const initial = React.useMemo(defaultWindow, []);
  const [from, setFrom] = React.useState(initial.from);
  const [to, setTo] = React.useState(initial.to);
  const { rows, meta, error, busy, load } = useArtefact<CrisisSummaryRow>("crisis/summary", { from, to });

  const columns = React.useMemo<GridColDef<CrisisSummaryRow & { id: string }>[]>(
    () => [
      { field: "day", headerName: "Day (UTC)", minWidth: 130, sortable: false },
      {
        field: "storyId",
        headerName: "Story",
        minWidth: 110,
        sortable: false,
        renderCell: ({ row }) =>
          row.storyId == null ? <span style={{ opacity: 0.6 }}>(no story)</span> : <code>{row.storyId}</code>,
      },
      { field: "fires", headerName: "Fires", minWidth: 100, align: "right", headerAlign: "right", sortable: false },
      {
        field: "classified",
        headerName: "With classification",
        minWidth: 170,
        align: "right",
        headerAlign: "right",
        sortable: false,
      },
    ],
    []
  );

  return (
    <Stack spacing={1}>
      <Typography variant="h6">Crisis events</Typography>
      <Typography variant="caption" color="text.secondary">
        Every time the crisis protocol fired and the reply was suppressed, written by the server as it forwarded the
        card (SB 243). No message content is stored; the classification column fills once core sends it.
      </Typography>
      <WindowPicker from={from} to={to} onFrom={setFrom} onTo={setTo} onRun={load} busy={busy} />
      {error && <Alert severity="error">{error}</Alert>}
      {rows && (
        <>
          <Chip
            size="small"
            variant="outlined"
            label={`${meta?.fires ?? 0} fire(s) across ${meta?.total ?? rows.length} day/story cell(s)`}
            sx={{ alignSelf: "flex-start" }}
          />
          <DataGrid
            rows={rows.map((row) => ({ ...row, id: `${row.day}:${row.storyId ?? "none"}` }))}
            columns={columns}
            autoHeight
            disableColumnFilter
            disableRowSelectionOnClick
            pageSizeOptions={[25, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          />
        </>
      )}
    </Stack>
  );
};

/** DSAR: one user's full consent ledger, oldest first. */
const ConsentHistory = () => {
  const [input, setInput] = React.useState("");
  const [userId, setUserId] = React.useState<string | null>(null);
  const { rows, error, busy, load } = useArtefact<ConsentEvent>(userId ? `consent/${userId}` : null);

  React.useEffect(() => {
    if (userId) {
      void load();
    }
  }, [userId, load]);

  const columns = React.useMemo<GridColDef<ConsentEvent>[]>(
    () => [
      {
        field: "at",
        headerName: "When",
        minWidth: 190,
        sortable: false,
        renderCell: ({ row }) => <span>{formatWhen(row.at)}</span>,
      },
      {
        field: "kind",
        headerName: "Event",
        minWidth: 160,
        sortable: false,
        renderCell: ({ row }) => (
          <Chip
            size="small"
            variant="outlined"
            color={row.kind === "mature_enable" ? "warning" : row.kind === "mature_disable" ? "default" : "info"}
            label={row.kind}
          />
        ),
      },
      {
        field: "sheetVersion",
        headerName: "Sheet",
        minWidth: 110,
        sortable: false,
        renderCell: ({ row }) => (row.sheetVersion ? <code>{row.sheetVersion}</code> : <span style={{ opacity: 0.6 }}>-</span>),
      },
      {
        field: "clientBuild",
        headerName: "Client",
        minWidth: 140,
        sortable: false,
        renderCell: ({ row }) =>
          row.clientBuild || row.clientPlatform ? (
            <span>{[row.clientPlatform, row.clientBuild].filter(Boolean).join(" · ")}</span>
          ) : (
            <span style={{ opacity: 0.6 }}>-</span>
          ),
      },
      {
        field: "sourceIp",
        headerName: "Source",
        minWidth: 150,
        sortable: false,
        renderCell: ({ row }) => (row.sourceIp ? <code>{row.sourceIp}</code> : <span style={{ opacity: 0.6 }}>-</span>),
      },
    ],
    []
  );

  return (
    <Stack spacing={1}>
      <Typography variant="h6">Consent history</Typography>
      <Typography variant="caption" color="text.secondary">
        One user's mature-content consent ledger, oldest first — the DSAR read. Rows are never edited or deleted;
        a deleted account leaves them behind anonymised.
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          size="small"
          label="User id"
          value={input}
          onChange={(event) => setInput(event.target.value.replace(/\D/g, ""))}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              setUserId(input || null);
            }
          }}
          sx={{ minWidth: 200 }}
        />
        <Button variant="outlined" size="small" onClick={() => setUserId(input || null)} disabled={busy}>
          Look up
        </Button>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {rows && userId && (
        <DataGrid
          rows={rows}
          columns={columns}
          autoHeight
          disableColumnFilter
          disableRowSelectionOnClick
          hideFooter
          localeText={{ noRowsLabel: "No consent events for this user" }}
        />
      )}
    </Stack>
  );
};

export const ComplianceList = () => (
  <List canCreate={false} title="Compliance evidence">
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary">
        The three records that evidence the client-side compliance work: the AI disclosure log (with the break
        reminders known minors get), the crisis event log and the mature-content consent ledger. Everything on this
        page is read-only — the tables behind it are append-only and nothing in the product edits or deletes a row.
      </Typography>
    </Box>
    <Stack spacing={4}>
      <DisclosureGaps />
      <BreakReminderGaps />
      <SessionTimeline />
      <CrisisSummary />
      <ConsentHistory />
    </Stack>
  </List>
);
